import { connect } from 'pwa-helpers';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import store, { waitForCart } from '../../../store/store';
import '../../product-card/product-card-display';
import { StoreState } from '../../../store/store.interface';
import {
  getAddToCartResponse,
  getCart as shopGetCart,
  getCartContext,
  getCartError,
  getCartStatus,
  isCartEmpty,
} from '../../cart/shop-cart/store/shop-cart.selectors';
import { BaseComponent } from '../../base-component/base-component';
import { booleanConverter } from '../../../common/converters';
import { BusinessUnit } from '../../checkout/shop-checkout.interface';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { PARAM_ISBN } from '../../search/thunks/search.thunk';
import { getApiErrorMessage } from '../../../common/utils/error-message.util';
import { getQueryParam } from '../../../common/utils/util';
import {
  getUserDataForUserAnalytics,
  isHeStudent,
  isProfessionalStudent,
  isSuccessOrFailureUser,
  selectUser,
} from '../../user-profile/user-profile.selectors';
import { addToCartUrlContext, getCartPageUrl, isHEInstructor } from '../../../common/utils/cart-util';
import {
  PROFESSIONAL_CR_EDUCATORS_URL,
  PROFESSIONAL_EDUCATORS_BOARDS_AND_BEYONDS_URL,
  PROFESSIONAL_FA4_EDUCATORS_URL,
  ProfessionalPrograms,
  professionalSteps,
} from '../../../common/constants/professional-redirects';
import { loading } from '../../../common/constants/api-constants';
import emit from '../../../common/events';
import { getShopCart } from '../../cart/shop-cart/thunks/get-shop-cart.thunk';
import { removeFromCart } from '../../cart/shop-cart/thunks/remove-from-cart.thunk';
import { addToCart } from '../../cart/shop-cart/thunks/add-to-cart.thunk';
import { isCustomerLoginIdleOrLoading, isCustomerLoginSuccess, loadCustomer } from '../../customer/customer';
import { getFreeTrialAvailability } from '../../free-trial/thunks/check-free-trial-availability.thunk';
import '../../free-trial/signedout-availability-free-trial.display';
import { getFreeTrialAvailabilityData } from '../../free-trial/store/free-trial.selector';
import '../../free-trial/signedin-availability-free-trial.display';
import { calculateDurationDigitalProduct, isProfessional, isSharpenPlus } from '../../../common/utils/product-util';
import { formatToMMDDYY } from '../../../common/utils/date-utils';
import { getFreeTrialAvailabilityGuest } from '../../free-trial/thunks/check-free-trial-availability-guest.thunk';
import { ActionStatus } from '../../../common/constants/action-status';
import { markReplacedProduct } from '../../free-trial/utils/free-trial-replacement.util';
import { saveCIDBoardsBeyondsOnLocalStorage } from '../../firstaid-forward/utils/boards-beyond.util';
import {
  getBaseTitle,
  getProductTypeBU,
  getProductTypeInfo,
  getSearchTermObjectFromCartLocalStorage,
  getSecondLevelBU,
  getTitleWithDuration,
  PRODUCT_TYPE_FLASHCARD,
  PROFESSIONAL_FCD_TITLE_TYPE,
} from '../../../common/utils/analytics-util';
import { setSelectedSharpenUpsellData } from '../../upsell/store/sharpen-product-data.store';
import {
  SHARPEN_LEARN_MORE_URL,
  SHARPEN_PROGRAM_ID,
  SHARPEN_SEO_TITLE_INITIALS,
} from '../../upsell/constants/sharpen-page-type.constants';
import { ApiError } from '../../../common/utils/api-utils';
import { getApiErrorCode } from '../../../common/utils/error-code-utils';
import { saveCartLocalStorageObject } from '../../search/utils/search.util';

export declare interface SubscriptionProductData {
  titleType?: string | null;
  division?: string | null;
  isbn: string;
  isbn13: string;
  mhid: string;
  productTitle: string;
  seoTitle: string;
  description: string;
  copyrightYear: string;
  duration: string;
  durationPeriod: string;
  listPrice: string;
  netPrice: string;
  availability: string;
  publishDate: string;
  contributors: string;
  instructorComp: string;
  isMaster: string;
  offerPricingId?: string;
  title?: string;
  edition?: number;
  everGreenProduct?: boolean;
}

const addToCartInstructorsLabels = {
  [ProfessionalPrograms.MKTSP0004701]: 'Step 1 For Instructors',
  [ProfessionalPrograms.MKTSP0004702]: 'Step 2 For Instructors',
  // boards and beyonds
  [ProfessionalPrograms.MKTSP009971000]: 'Step 1 For Instructors',
  [ProfessionalPrograms.MKTSP009971001]: 'Step 1 Preclinical For Instructors',
  [ProfessionalPrograms.MKTSP009971002]: 'Step 2-3 Clinical For Instructors',
  [ProfessionalPrograms.MKTSP009971003]: 'Clinical Confidence For Instructors',
  [ProfessionalPrograms.MKTSP009971004]: 'The OMM Series For Instructors',
  // sharpen
  [SHARPEN_PROGRAM_ID]: 'Learn More',
  // clinical reasoning
  [ProfessionalPrograms.MKTSP000471001]: 'CR For Instructors',
};

@customElement('mhecomm-subscription-add-to-cart')
export class SubscriptionAddToCart extends connect(store)(BaseComponent) {
  /**
   * Which store this product is for. This parameter is optional.
   */
  @property()
  store?: string;

  /**
   * If true, the user will be taken directly to the checkout page after clicking the add to cart button.
   */
  @property({ converter: booleanConverter })
  instantCheckout = false;

  /**
   * The business unit of the provided ISBN. This is a required propery.
   * Default is Professional
   */
  @property()
  businessUnit: BusinessUnit = BusinessUnit.PROFESSIONAL;

  /**
   * The ISBN of the product that you want the user to be able to remove to their cart. This is a required propery.
   */
  @property()
  isbn = '';

  @property()
  isbn13 = '';

  /**
   * The text to display when this product is not yet in the cart.
   */
  @property()
  text = 'Add to cart';

  /**
   * professional program id
   */
  @property()
  programId = '';

  /**
   * A JSON String in the format of 'subscriptionProductData'.
   */
  @property({ type: Array })
  productData?: Array<SubscriptionProductData>;

  /**
   * To check if the component is being called from sharpen upsell widget component
   */
  @property({ type: Boolean })
  isCalledFromSharpenUpselWidget = false;

  /*
   * An additional free flowing object
   * that can be passed to the component
   * to be propagated to the DL on added to cart
   */
  @property({type: Object})
  addToCartAdditionalAnalytics = {}

  /**
   * Capture Event in local storage for analytics
   */
  @property({ converter: booleanConverter })
  capturePDPLocalStorage = false;

  @state()
  hasFreeTrial = false;

  @state()
  freeTrialDuration = 0;

  @state()
  selectedProduct?: SubscriptionProductData;

  @state()
  buttonText = '';

  @state()
  private _addedToCart = false;

  @state()
  private isHEInstructor = false;

  @state()
  private stateIsDisabled = false;

  @state()
  private freetrialChecked = false;

  @state()
  private userIsEligibleForFreetrial = false;

  @state()
  private addToCartFailed = false;

  private user?: UserProfile;

  private dropDownEventGate = false;

  connectedCallback() {
    super.connectedCallback();
    if (this.productData?.length) {
      const isbn = getQueryParam(PARAM_ISBN);
      if (isbn) {
        this.selectedProduct = this.productData?.find((value) => value.isbn === isbn || value.isbn13 === isbn);
      }

      if (!this.selectedProduct) {
        this.selectedProduct = this.productData[0];
      }

      // this is save the first product from product data as selected sharpen product, when this component is called from sharpen PDP page
      if (this.selectedProduct) {
        store.dispatch(setSelectedSharpenUpsellData(this.selectedProduct));
      }
      this.determineBtnText(store.getState() as StoreState);
    }
    store.dispatch(loadCustomer());
    store.dispatch(getShopCart({ store: this.store }));
    this.getUserEligibilityForFreeTrial(store.getState() as StoreState);
    saveCIDBoardsBeyondsOnLocalStorage();
  }

  stateChanged(state: StoreState) {
    this.determineBtnText(state);
    this.user = selectUser(state);
    this.isHEInstructor = isHEInstructor(this.user);
    this.getUserEligibilityForFreeTrial(state);
  }

  getUserEligibilityForFreeTrial(state: StoreState) {
    if (this.freetrialChecked || !isSuccessOrFailureUser(state)) {
      return;
    }

    if (this.user && !this.isEligibleUser(state)) {
      this.hasFreeTrial = false;
      this.freetrialChecked = true;
      return;
    }

    if (isCustomerLoginIdleOrLoading(state) || !this.selectedProduct) {
      return;
    }

    this.checkAvailability(state);
  }

  isEligibleUser(state: StoreState) {
    return isHeStudent(state) || isProfessionalStudent(state);
  }

  checkAvailability(state: StoreState) {
    if (isCustomerLoginSuccess(state)) {
      store
        .dispatch(getFreeTrialAvailability({ skus: [`OP${this.selectedProduct?.isbn13}`] }))
        .then(() => this.setAvailabilityInternalState());
      return;
    }

    store
      .dispatch(getFreeTrialAvailabilityGuest({ skus: [`OP${this.selectedProduct?.isbn13}`] }))
      .then(() => this.setAvailabilityInternalState());
  }

  setAvailabilityInternalState() {
    const availability = getFreeTrialAvailabilityData(store.getState() as StoreState);
    this.freetrialChecked = true;
    const freeTrial = availability?.freeTrialAvailability[0];
    if (!freeTrial) {
      this.hasFreeTrial = false;
      return;
    }
    this.hasFreeTrial = freeTrial.enabled;
    this.freeTrialDuration = freeTrial.duration ? freeTrial.duration : 0;
    this.userIsEligibleForFreetrial = freeTrial.enabled;
  }

  async determineBtnText(state: StoreState) {
    await waitForCart();

    if (this.selectedProduct?.availability === 'AVAILABLE') {
      this.stateIsDisabled = false;
    } else {
      this.stateIsDisabled = true;
    }

    if (this.isHEInstructor) {
      this.stateIsDisabled = false;
      this._addedToCart = false;
    } else {
      this.getSelectedProductFromCart(state);
    }

    this.buttonText = this.getButtonText();
  }

  /**
   * The text of the button that is currently being displayed.
   */
  getButtonText() {
    if (this.isHEInstructor && addToCartInstructorsLabels[this.programId]) {
      return addToCartInstructorsLabels[this.programId];
    } else if (this._addedToCart) {
      return 'In Cart';
    } else {
      return this.text;
    }
  }

  /*
   * The redirect url for HE Instructors based pn program ids
   */
  getButtonRedirectUri() {
    switch (this.programId) {
      // sharpen
      case SHARPEN_PROGRAM_ID:
        return SHARPEN_LEARN_MORE_URL;
      // boards and beyonds
      case ProfessionalPrograms.MKTSP009971000:
      case ProfessionalPrograms.MKTSP009971001:
      case ProfessionalPrograms.MKTSP009971002:
      case ProfessionalPrograms.MKTSP009971003:
      case ProfessionalPrograms.MKTSP009971004:
        return PROFESSIONAL_EDUCATORS_BOARDS_AND_BEYONDS_URL;
      case ProfessionalPrograms.MKTSP0004701:
      case ProfessionalPrograms.MKTSP0004702:
        return PROFESSIONAL_FA4_EDUCATORS_URL;
      case ProfessionalPrograms.MKTSP000471001:
        return PROFESSIONAL_CR_EDUCATORS_URL;
      default:
        return PROFESSIONAL_FA4_EDUCATORS_URL;
    }
  }

  getSelectedProductFromCart(state: StoreState) {
    if (this.selectedProduct && !isCartEmpty(state)) {
      const cartProductItem = shopGetCart(state)?.products.find(
        (product) => {
          // if items on cart are from edoms, use offerPricingId to compare
          if (product.productIdentifier.offerPricingId) {
            return product.productIdentifier.offerPricingId === this.selectedProduct?.offerPricingId;
          }
          
          return product.productIdentifier.isbn10 === this.selectedProduct?.isbn ||
            product.productIdentifier.isbn13 === this.selectedProduct?.isbn13
      });

      if (cartProductItem) {
        this._addedToCart = true;
        this.stateIsDisabled = true;
      } else {
        this._addedToCart = false;
      }
    }
  }

  renderComp() {
    return html` ${this.addToCartFailed ? this.renderErrorToast() : nothing} ${this.renderAddToCart()} `;
  }

  private renderErrorToast() {
    return html`
      <mhecomm-toast
        type="primary"
        header="Error Adding Item to Cart"
        .body=${this.getErrorMessage()}
        ?autoHide="${false}"
        @toast-close="${() => (this.addToCartFailed = false)}"
      ></mhecomm-toast>
    `;
  }

  private getErrorMessage() {
    const apiError = getCartError(store.getState() as StoreState) as ApiError;
    return getApiErrorMessage({ bold: true, errorCode: getApiErrorCode(apiError)});
  }

  private renderAddToCart() {
    return html`${this.isCalledFromSharpenUpselWidget 
      ? this.renderSharpenAddToCart()
      : this.renderBAndBAddToCart()
    }`;
  }

  renderSharpenAddToCart() {
    return html`
      <div part="container">
        <div class="col-12">
          <label class="form-label fw-semibold" for="select-subscription"
            >Select Access Duration:</label
          >
        </div>
        <div class="row">
          <div class="col-12 col-md-9">
            <div style="display:inline">
              <select
                id="select-subscription"
                name="subscription"
                class="form-select"
                @click=${this.emitClickSubscriptionOptions}
                @focus=${this.emitClickSubscriptionOptions}
                @change=${(_e) => this._updateSelectedProduct(_e, (_e.target as HTMLInputElement).value)}
              >
                ${this.productData?.map(
                  (product) =>
                    html` <option value="${this.getProductIdentifier(product)}" ?selected=${this.isProductSelected(product)}>
                      ${product.duration +
                      '-' +
                      product.durationPeriod +
                      `${this.isCalledFromSharpenUpselWidget ? 's' : ''}` +
                      ' Access' +
                      ' - $' +
                      parseFloat(product.netPrice).toFixed(2)}
                    </option>`
                )}
              </select>
            </div>
          </div>
          <div class="col-12 col-md-3 mt-2 mt-md-0">
            ${this.buttonText && !isCustomerLoginIdleOrLoading(store.getState() as StoreState)
              ? html` <button
                  class="btn btn-primary w-100"
                  @click=${this._onClick}
                  data-automation-id="add-to-cart-${this.selectedProduct?.isbn}"
                  ?disabled=${this.stateIsDisabled}
                >
                  ${this.buttonText}
                </button>`
              : loading({ columns: 1, m: 'mt-0', width: '100%' })}
          </div>
        </div>
      </div>
    `;
  }

  renderBAndBAddToCart() {
    return html` <div part="container">
      <div class="col-12">
        <label class="form-label" for="select-subscription"
          >Select ${isSharpenPlus(this.programId) ? 'Access' : 'Subscription'} Duration:</label
        >
        <div style="display:inline">
          <select
            id="select-subscription"
            name="subscription"
            class="form-select"
            @click=${this.emitClickSubscriptionOptions}
            @focus=${this.emitClickSubscriptionOptions}
            @change=${(_e) => this._updateSelectedProduct(_e, (_e.target as HTMLInputElement).value)}
          >
            ${this.productData?.map(
              (product) =>
                html` <option value="${this.getProductIdentifier(product)}" ?selected=${this.isProductSelected(product)}>
                  ${product.duration +
                  '-' +
                  product.durationPeriod +
                  ' Access' +
                  ' - $' +
                  parseFloat(product.netPrice).toFixed(2)}
                </option>`
            )}
          </select>
        </div>
        <div class="mt-2" data-automation-id="free-trial-verbiage">
          ${this.getFreetrialVerbiage()}
        </div>
      </div>
      ${this.selectedProduct
        ? html` <div class="col-12">
            <div class="row pt-3">
              <div class="col-12 col-md-7">
                <div class="d-flex justify-content-center flex-column">
                  <div class="text-center text-md-start">
                    <p
                      class="fs-2 fw-semibold mb-0"
                      aria-live="polite"
                      aria-atomic="true"
                      aria-label="Product price updated to"
                      data-automation-id="price-to-pay"
                    >
                      ${this.getPricingToShow()}
                    </p>
                  </div>
                  ${this.getDetailedBillingMessage()}
                </div>
              </div>
              <div class="col-12 col-md-5 mt-2 mt-md-0">
                <div class="d-flex justify-content-center">
                  ${this.buttonText && !isCustomerLoginIdleOrLoading(store.getState() as StoreState)
                    ? html` <button
                        class="btn btn-primary w-100"
                        @click=${this._onClick}
                        data-automation-id="add-to-cart-${this.selectedProduct?.isbn}"
                        ?disabled=${this.stateIsDisabled}
                      >
                        ${this.buttonText}
                      </button>`
                    : loading({ columns: 1, m: 'mt-0', width: '100%' })}
                </div>
              </div>
            </div>
          </div>`
        : nothing}
    </div>`;
  }

  getProductIdentifier(product: SubscriptionProductData) {
    return product.offerPricingId || product.isbn;
  }

  isProductSelected(product: SubscriptionProductData) {
    return this.getProductIdentifier(product) === this.getProductIdentifier(this.selectedProduct!)
  }

  getPricingToShow() {
    if (this.userIsEligibleForFreetrial) {
      return `$0.00 Today`;
    }
    return `$${parseFloat(this.selectedProduct!.netPrice).toFixed(2)}`;
  }

  private getFreetrialVerbiage() {
    if (!this.hasFreeTrial) {
      return nothing;
    }

    if (!isCustomerLoginSuccess(store.getState() as StoreState)) {
      return html` <mhecomm-signedout-availability-free-trial
        .isbn=${this.selectedProduct?.isbn13}
        .programId=${this.programId}
        .freeTrialDuration=${this.freeTrialDuration}
      ></mhecomm-signedout-availability-free-trial>`;
    }

    if (this.isUserEligibleForFreetrial()) {
      return html` <mhecomm-signedin-availability-free-trial
        .freeTrialDuration=${this.freeTrialDuration}
      ></mhecomm-signedin-availability-free-trial>`;
    }
    return nothing;
  }

  private isUserEligibleForFreetrial() {
    if (!this.freetrialChecked) {
      return false;
    }

    const eligibility = getFreeTrialAvailabilityData(store.getState() as StoreState);
    if (eligibility && eligibility.freeTrialAvailability?.length > 0) {
      this.userIsEligibleForFreetrial = eligibility.freeTrialAvailability[0].enabled;
      return this.userIsEligibleForFreetrial;
    }
    return false;
  }

  private getDetailedBillingMessage() {
    if (!this.hasFreeTrial || isCustomerLoginIdleOrLoading(store.getState() as StoreState)) {
      return nothing;
    }

    if (!isCustomerLoginSuccess(store.getState() as StoreState)) {
      return html` <div
        class="text-center text-md-start fw-lighter fst-italic"
        data-automation-id="detailed-billing-message"
      >
        Automatically billed at end of ${this.freeTrialDuration}-Day free trial (if applicable). Otherwise, billed at
        time of purchase.
      </div>`;
    }

    if (this.isUserEligibleForFreetrial()) {
      return html` <div
        class="text-center text-md-start fw-ligher fst-italic"
        data-automation-id="detailed-billing-message"
      >
        $${this.selectedProduct?.netPrice} + taxes and fees automatically billed at end of free trial on
        ${this.getEndDateFreetrial()}
      </div>`;
    }

    return nothing;
  }

  private getEndDateFreetrial() {
    return formatToMMDDYY(calculateDurationDigitalProduct(this.freeTrialDuration, 'Day'));
  }

  private _updateSelectedProduct(event, value) {
    const selected = String(value);
    this.selectedProduct = this.productData?.find((product) => {
      if (product.offerPricingId) {
        return product.offerPricingId === selected;
      }
      return product.isbn === selected;
      }
    );
    if (this.selectedProduct) {
      store.dispatch(setSelectedSharpenUpsellData(this.selectedProduct));
    }
    this.determineBtnText(store.getState() as StoreState);
    if (event.target === event.currentTarget) {
      this.dropDownEventGate = true;
      setTimeout(() => {
        this.dropDownEventGate = false;
      }, 500);
      this.emitSubscriptionSelected();
    }
  }

  private _onClick(_e: Event) {
    saveCartLocalStorageObject({
      programSourceId: this.programId!,
      capturePDPLocalStorage: this.capturePDPLocalStorage,
      isbn10: this.selectedProduct?.isbn!,
      isbn13: this.selectedProduct?.isbn13!,
    });
    if (this.isHEInstructor) {
      emit({
        element: this,
        name: 'click',
        detail: {
          eventName: 'instructor-click',
          label: this.buttonText,
        },
      });

      // The delay has been added for the network call to be successful
      // otherwise the network call will get cancelled if the redirection starts
      // before that
      setTimeout(() => window.location.assign(this.getButtonRedirectUri()), 300);
    } else if (!this._addedToCart && this.selectedProduct) {
      store
        .dispatch(
          addToCart(
            addToCartUrlContext(getCartContext(store.getState() as StoreState, this.store), {
              quantity: 1,
              businessUnit: this.isCalledFromSharpenUpselWidget ? BusinessUnit.HE : this.businessUnit,
              isbn: this.selectedProduct?.isbn!,
              isbn13: this.selectedProduct?.isbn13,
              freeTrial: this.isUserEligibleForFreetrial(),
              offerPricingId: this.selectedProduct?.offerPricingId,
            })
          )
        )
        .then(this.handleResponse.bind(this));
      this.determineBtnText(store.getState() as StoreState);
    } else {
      store.dispatch(
        removeFromCart(
          addToCartUrlContext(getCartContext(store.getState() as StoreState, this.store), {
            quantity: 1,
            businessUnit: this.isCalledFromSharpenUpselWidget ? BusinessUnit.HE : this.businessUnit,
            isbn: this.isbn,
            isbn13: this.isbn13,
            offerPricingId: this.selectedProduct?.offerPricingId,
          })
        )
      );
      this.emitRemovedFromCart(this.isUserEligibleForFreetrial());
    }
  }

  emitClickSubscriptionOptions(event) {
    if (event.sourceCapabilities === null) {
      return;
    }
    if (event.type === 'focus' && !this.dropDownEventGate) {
      this.dropDownEventGate = true;
      setTimeout(() => {
        this.dropDownEventGate = false;
      }, 500);
    }
    if (event.type === 'click' && this.dropDownEventGate) {
      this.dropDownEventGate = false;
      return;
    }
    emit({
      element: this,
      name: 'click',
      detail: {
        label: `${this.getStep()} Dropdown`,
        url: window.location.href,
        eventName: 'subscription-options-dropdown',
      },
      dataLayer: true,
    });
  }

  getProfessionalStep() {
    return professionalSteps[this.programId].step;
  }

  getSharpenStep() {
    return `Sharpen Plus`
  }

  getStep() {
    return isSharpenPlus(this.programId) ? this.getSharpenStep() : this.getProfessionalStep()
  }

  emitSubscriptionSelected() {
    emit({
      element: this,
      name: 'click',
      detail: {
        ...this.dataLayerDetail(this.isUserEligibleForFreetrial(), {
          ...this.addToCartAdditionalAnalytics,
          ...(this.checkSharpenProduct() && { label: 'Sharpen Plus Dropdown' }),
        }),
        eventName: 'subscription-option',
      },
      dataLayer: true,
    });
  }

  emitAddToCart(isFreeTrial) {
    emit({
      element: this,
      name: 'added-to-cart',
      detail: this.dataLayerDetail(isFreeTrial, this.addToCartAdditionalAnalytics),
      dataLayer: true,
    });
  }

  emitRemovedFromCart(isFreeTrial) {
    emit({
      element: this,
      name: 'removed-from-cart',
      detail: this.dataLayerDetail(isFreeTrial),
    });
  }

  private dataLayerDetail(isFreeTrial, additionalData: Record<string, any> = {}) {
    const state = store.getState() as StoreState;

    return JSON.parse(
      JSON.stringify({
        store: this.store,
        url: window.location.href,
        isbn: this.selectedProduct?.isbn,
        isbn13: this.selectedProduct?.isbn13,
        title: this.selectedProduct?.productTitle,
        seoTitle: this.selectedProduct?.seoTitle,
        description: this.selectedProduct?.description,
        price: this.selectedProduct?.listPrice,
        offerPricingId: this.selectedProduct?.offerPricingId,
        isEdoms: !!this.selectedProduct?.offerPricingId,
        quantity: 1,
        duration: this.selectedProduct?.duration,
        durationPeriod: this.selectedProduct?.durationPeriod,
        user: getUserDataForUserAnalytics(state),
        businessUnit: this.businessUnit,
        copyrightYear: this.selectedProduct?.copyrightYear,
        publishDate: this.selectedProduct?.publishDate,
        contributors: this.selectedProduct?.contributors,
        availability: this.selectedProduct?.availability,
        instructorComp: this.selectedProduct?.instructorComp,
        isMaster: this.selectedProduct?.isMaster,
        productOrdered: isFreeTrial ? 'freetrial' : 'purchasable',
        ...getProductTypeBU({
          programIds: [this.programId],
        }),
        ...getSecondLevelBU({
          programId: this.programId,
        }),
        ...getProductTypeInfo({
          programId: this.programId
        }),
        ...additionalData,
        ...getSearchTermObjectFromCartLocalStorage({
          programId: this.programId ?? '',
          showRemoveLink: false,
        }),
        ...getTitleWithDuration({
          title: getBaseTitle({
            product: {},
            state,
            productTitle: this.selectedProduct?.title ?? this.selectedProduct?.productTitle ?? '',
            edition: this.selectedProduct?.edition,
            isEvergreenProduct: this.selectedProduct?.everGreenProduct,
            programSourceId: this.programId
          }) ?? '',
          duration: Number(this.selectedProduct?.duration!),
          durationPeriod: this.selectedProduct?.durationPeriod!,
          isHeDigitalProduct: this.checkSharpenProduct(),
        }),
        ...(this.selectedProduct?.titleType === PROFESSIONAL_FCD_TITLE_TYPE && {
          productType: PRODUCT_TYPE_FLASHCARD,
        }),
      })
    );
  }

  private checkSharpenProduct() {
    return this.selectedProduct?.seoTitle?.includes(SHARPEN_SEO_TITLE_INITIALS);
  }

  private async handleResponse() {
    await waitForCart();
    const state = store.getState() as StoreState;
    markReplacedProduct(getAddToCartResponse(state));

    if (getCartStatus(state) === ActionStatus.failed) {
      this.addToCartFailed = true;
    } else {
      const redirectUrl: string = getCartPageUrl(this.user, this.store);
      this.emitAddToCart(this.isUserEligibleForFreetrial());
      this.doRedirect(redirectUrl);
    }
  }

  public doRedirect(redirectUrl: string) {
    if (this.instantCheckout) {
      window.location.href = redirectUrl;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-subscription-add-to-cart': SubscriptionAddToCart;
  }
}
